/**
 * AdminForbidden route (025-admin-area T004) — the `/forbidden` surface a
 * member lands on when they hit an admin-only route.
 *
 * Routing behaviour: registered at `/forbidden` as `protected: true` with
 * NO `role`, in the member `<Layout>` chrome (the default). It is the
 * `RouteGuard` member-on-admin bounce target (FR-010, research RD-002):
 * when a member-kind session hits a route marked `role: 'admin'`, the
 * guard redirects here and the member reads the denial prose. Because the
 * destination carries no `role`, the bounce lands cleanly and does not
 * trigger a second redirect. `protected: true` keeps an anonymous visitor
 * who types `/forbidden` bouncing to `/login` first (the universal
 * sign-in prerequisite) rather than reading a denial they have no session
 * for.
 *
 * Why member chrome and no back-to-portal: a member has no admin portal to
 * return to, so the surface mounts the member `<Layout>` (its `PrimaryNav`
 * owns wayfinding) and the copy points back to the primary navigation
 * ("Palaa etusivulle päänavigaatiosta"). There is deliberately no
 * back-to-portal affordance — that affordance belongs to the portal-styled
 * admin sections, not to a member-facing denial.
 *
 * The copy is the denial prose under the `admin.forbidden.*` keys
 * (`i18n/messages/{fi,en}/admin.json`), carried over from the retired
 * `/admin` demo-403 surface (replaced by the launch portal in
 * 025-admin-area US1). This surface owns those strings outright.
 *
 * This is a non-interactive prose surface: it takes no session read of its
 * own, makes no navigate decision, and renders no interactive chrome (no
 * buttons, no inputs, no NavLinks) — the shell's `PrimaryNav` owns
 * navigation.
 */

import type { JSX } from 'react';

import { useTranslate } from '../i18n/useTranslate.js';
import { Card } from '../primitives/Card.js';

const pageClasses = 'flex flex-col gap-4';
const headingClasses = 'm-0 text-xl font-semibold';
const bodyClasses = 'm-0 text-base text-foreground';

// `Card` (not a bare `<section>`) mirrors the other prose routes: the route
// renders inside `Layout`'s `<main>` landmark and the primitive supplies the
// token-backed surface treatment. The internal heading starts at `<h2>`
// because `Layout` owns the shell's `<h1>`.
export function AdminForbidden(): JSX.Element {
  const t = useTranslate();

  return (
    <Card className={pageClasses}>
      <h2 className={headingClasses}>{t('admin.forbidden.heading')}</h2>
      <p className={bodyClasses}>{t('admin.forbidden.body')}</p>
      <p className={bodyClasses}>{t('admin.forbidden.navigation')}</p>
    </Card>
  );
}
